import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { API } from '../App';

/**
 * Iter88l — Forgot Password
 *
 * Posts the email to /api/auth/forgot-password. The backend always returns
 * 200 to prevent email enumeration; we show the same confirmation message
 * regardless and ask the user to check their inbox.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      // The endpoint always returns 200 by design; anything reaching here is
      // a network/CORS issue — keep the message vague to avoid enumeration.
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-6 text-white">
          <h2 className="text-2xl font-bold">Reset your password</h2>
          <p className="text-emerald-50 text-sm mt-1">
            We&apos;ll email you a secure link to set a new password.
          </p>
        </div>

        <div className="p-8">
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-password-sent">
              <div className="flex justify-center">
                <div className="rounded-full bg-emerald-100 dark:bg-emerald-900/40 p-3">
                  <CheckCircle2 className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Check your inbox</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                If an account exists for <strong>{email}</strong>, a reset link is on its way.
                The link will expire in 30 minutes.
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Didn&apos;t get an email? Check spam, or wait a minute and try again.
              </p>
              <div className="pt-4 flex gap-2 justify-center">
                <Button
                  variant="outline"
                  onClick={() => { setSent(false); setEmail(''); }}
                  data-testid="forgot-password-try-again-btn"
                >
                  Try again
                </Button>
                <Button
                  onClick={() => navigate('/login')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="back-to-login-btn"
                >
                  Back to sign in
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
              <div>
                <Label htmlFor="email" className="text-slate-700 dark:text-slate-300 font-medium">
                  Email address
                </Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    autoFocus
                    autoComplete="email"
                    data-testid="forgot-password-email-input"
                    className="pl-10 h-11 rounded-xl"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="forgot-password-submit-btn"
                className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
